import { useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@chikara/ui';
import avatarAdminUrl from '../../assets/avatar-admin.svg';
import avatarBasicUrl from '../../assets/avatar-basic.svg';
import spendingLimitInfoUrl from '../../assets/spending-limit-info.svg';
import styles from './UserFormModal.module.css';
import { t } from '@/i18n';

export interface UserFormModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Modal description */
  description: string;
  /** Form content (fields) */
  children: ReactNode;
  /** Primary action button label */
  primaryActionLabel: string;
  /** Primary action callback */
  onPrimaryAction: () => void;
  /** Secondary action button label (optional) */
  secondaryActionLabel?: string;
  /** Secondary action callback (optional) */
  onSecondaryAction?: () => void;
  /** Secondary action variant */
  secondaryActionVariant?: 'outline' | 'link-danger';
  /** Whether primary action is loading */
  isLoading?: boolean;
  /** Whether primary action is disabled */
  isPrimaryDisabled?: boolean;
}

export function UserFormModal({
  isOpen,
  onClose,
  title,
  description,
  children,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionVariant = 'outline',
  isLoading = false,
  isPrimaryDisabled = false,
}: UserFormModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const body = document.body;
      const openCount = parseInt(body.getAttribute('data-modal-open-count') || '0', 10);
      
      // If this is the first modal, save the original overflow
      if (openCount === 0) {
        body.setAttribute('data-original-overflow', body.style.overflow);
        body.style.overflow = 'hidden';
      }
      
      // Increment the count
      body.setAttribute('data-modal-open-count', (openCount + 1).toString());

      return () => {
        const currentCount = parseInt(body.getAttribute('data-modal-open-count') || '1', 10);
        const newCount = Math.max(0, currentCount - 1);
        body.setAttribute('data-modal-open-count', newCount.toString());
        
        // If this was the last modal, restore the original overflow
        if (newCount === 0) {
          body.style.overflow = body.getAttribute('data-original-overflow') || '';
          body.removeAttribute('data-original-overflow');
        }
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      const timer = setTimeout(() => {
        modalRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else if (previousActiveElement.current) {
      previousActiveElement.current.focus();
      previousActiveElement.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const isClickInsideRef = useRef(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    isClickInsideRef.current = modalRef.current?.contains(e.target as Node) ?? false;
  };

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget && !isClickInsideRef.current) {
        onClose();
      }
    },
    [onClose]
  );

  if (!shouldRender) return null;

  const overlayClasses = [styles.overlay, isAnimating ? styles.open : ''].filter(Boolean).join(' ');
  const modalClasses = [styles.modal, isAnimating ? styles.open : ''].filter(Boolean).join(' ');

  const modalContent = (
    <div className={overlayClasses} onMouseDown={handleMouseDown} onClick={handleOverlayClick}>
      <div
        ref={modalRef}
        className={modalClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-form-modal-title"
        tabIndex={-1}
      >
        {/* Left Column - Form */}
        <div className={styles.formColumn}>
          <div className={styles.formHeader}>
            <h2 id="user-form-modal-title" className={styles.title}>
              {title}
            </h2>
            <p className={styles.description}>
              {description}
            </p>
          </div>

          <div className={styles.formContent}>
            {children}
          </div>

          <div className={styles.formActions}>
            <Button
              variant="primary"
              onClick={onPrimaryAction}
              isLoading={isLoading}
              disabled={isPrimaryDisabled || isLoading}
              fullWidth
            >
              {primaryActionLabel}
            </Button>
            {secondaryActionLabel && onSecondaryAction && (
              secondaryActionVariant === 'link-danger' ? (
                <Button
                  variant="link-tertiary"
                  onClick={onSecondaryAction}
                  disabled={isLoading}
                  fullWidth
                  className={styles.dangerLink}
                >
                  {secondaryActionLabel}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={onSecondaryAction}
                  disabled={isLoading}
                  fullWidth
                >
                  {secondaryActionLabel}
                </Button>
              )
            )}
          </div>
        </div>

        {/* Right Column - Sidebar Info */}
        <div className={styles.sidebarColumn}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Cerrar modal"
          >
            <CloseIcon />
          </button>

          <div className={styles.sidebarContent}>
            {/* User Types Section */}
            <section className={styles.sidebarSection}>
              <h3 className={styles.sidebarSectionTitle}>
                {t('team.sidebar.userTypesTitle')}
              </h3>
              <div className={styles.userRolesList}>
                <div className={styles.userRoleItem}>
                  <div className={styles.userRoleAvatar}>
                    <img src={avatarAdminUrl} alt="" width={50} height={50} />
                  </div>
                  <div className={styles.userRoleInfo}>
                    <span className={styles.userRoleName}>
                      {t('team.user.role.admin')}
                    </span>
                    <p className={styles.userRoleDescription}>
                      {t('team.sidebar.adminDescription')}
                    </p>
                  </div>
                </div>
                <div className={styles.userRoleItem}>
                  <div className={styles.userRoleAvatar}>
                    <img src={avatarBasicUrl} alt="" width={50} height={50} />
                  </div>
                  <div className={styles.userRoleInfo}>
                    <span className={styles.userRoleName}>
                      {t('team.user.role.basic')}
                    </span>
                    <p className={styles.userRoleDescription}>
                      {t('team.sidebar.basicDescription')}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Spending Limits Section */}
            <section className={styles.sidebarSection}>
              <h3 className={styles.sidebarSectionTitle}>
                {t('team.sidebar.spendingLimitsTitle')}
              </h3>
              <div className={styles.spendingLimitsContent}>
                <div className={styles.spendingLimitsIcon}>
                  <img src={spendingLimitInfoUrl} alt="" />
                </div>
                <p className={styles.spendingLimitsDescription}>
                  {t('team.sidebar.spendingLimitsDescription')}
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// Internal close icon component
function CloseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M1 1L13 13M13 1L1 13"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
